import { context as otelContext, trace } from '@opentelemetry/api';
import isNil from 'lodash/isNil';
import posthog from 'posthog-js';

/**
 * Reads the currently active OpenTelemetry span (if any) and returns its
 * trace/span IDs so the captured PostHog exception can be cross-referenced
 * with the corresponding Grafana trace.
 *
 * Returns an empty object when tracing is disabled or no span is active.
 */
function getActiveTraceContext(): { traceId?: string; spanId?: string } {
  try {
    const activeSpan = trace.getSpan(otelContext.active());
    const spanContext = activeSpan?.spanContext();

    if (
      isNil(spanContext) ||
      isNil(spanContext.traceId) ||
      spanContext.traceId === '00000000000000000000000000000000'
    ) {
      return {};
    }

    return {
      traceId: spanContext.traceId,
      spanId: spanContext.spanId,
    };
  } catch {
    return {};
  }
}

/**
 * Captures a handled exception in PostHog with semantic context.
 *
 * Use this when an error has been caught and dealt with by the application
 * (logged, snackbar shown, fallback rendered, etc.) but should still be
 * visible in PostHog for product debugging.
 *
 * For unhandled exceptions, the global `capture_exceptions: true` config
 * and `PostHogErrorBoundary` already handle capture automatically.
 *
 * Note: GraphQL/network errors flowing through Apollo are captured at the
 * `HasuraApiProvider` errorLink chokepoint. Do NOT call this from individual
 * mutation `onError` handlers — it would double-capture.
 *
 * When OpenTelemetry tracing is enabled, the active span's `traceId` and
 * `spanId` are automatically attached so the PostHog exception can be linked
 * to its corresponding trace in Grafana (and to the backend span tree, since
 * the trace context is propagated via the `traceparent` header to `*.predium.de`).
 */
export function captureClientException(error: unknown, context?: Record<string, unknown>) {
  const traceContext = getActiveTraceContext();

  try {
    posthog?.captureException(error, {
      ...traceContext,
      ...context,
    });
  } catch (e) {
    console.error('Failed to capture exception in PostHog', e);
  }
  console.error(error);
}
